import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useHotel } from "@/context/HotelContext";
import { useAuth } from "@/context/AuthContext";
import { isGlobal } from "@/lib/constants";

const opts = (o) => (o || []).map((x) => (typeof x === "string" ? { value: x, label: x } : x));

const Field = ({ f, value, onChange, hotelId, testid }) => {
  const { hotels, staffFor } = useHotel();
  const common = { "data-testid": testid, value: value ?? "", onChange: (e) => onChange(e.target.value) };
  if (f.type === "textarea") return <Textarea rows={3} {...common} />;
  if (["text", "date", "number", "datetime", undefined].includes(f.type))
    return <Input type={f.type === "datetime" ? "datetime-local" : f.type || "text"} {...common} />;
  const options = f.type === "hotel" ? hotels.map((h) => ({ value: h.id, label: h.name }))
    : f.type === "staff" ? staffFor(hotelId).map((s) => ({ value: s.id, label: `${s.name} · ${s.role}` }))
    : opts(f.options);
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const ResourceForm = ({ open, onClose, onSubmit, fields, initial, title, testid = "resource-form" }) => {
  const { user } = useAuth();
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(initial || {}); }, [open, initial]);

  const visible = fields.filter((f) => !(f.type === "hotel" && !isGlobal(user) && !f.always));

  const submit = async (e) => {
    e.preventDefault();
    const missing = visible.filter((f) => f.required && !v[f.key]);
    if (missing.length) return toast.error(`Compila: ${missing.map((m) => m.label).join(", ")}`);
    setBusy(true);
    try { await onSubmit(v); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          <DialogDescription className="text-slate-500">I campi con * sono obbligatori.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visible.map((f) => (
            <div key={f.key} className={f.type === "textarea" || f.full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label className="text-xs text-slate-500">{f.label}{f.required && " *"}</Label>
              <Field f={f} value={v[f.key]} hotelId={v.hotel_id} testid={`${testid}-${f.key}`}
                onChange={(val) => setV((p) => ({ ...p, [f.key]: val }))} />
            </div>
          ))}
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid={`${testid}-cancel`}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{busy ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
